const supportsServiceWorker = typeof window !== 'undefined' && 'serviceWorker' in navigator;

/** Default cap so workspace load never waits forever on a stuck `register()` promise. */
const DEFAULT_WORKSPACE_SW_ENSURE_TIMEOUT_MS = 5000;

let registrationPromise = null;
let registrationStatus = null; // null = not attempted, true = success, false = failed

export function ensureWorkspaceServiceWorker() {
  if (!supportsServiceWorker) {
    registrationStatus = false; // Not supported
    return Promise.resolve(null);
  }
  if (registrationPromise) {
    return registrationPromise;
  }
  registrationPromise = navigator.serviceWorker.register('/workspace-sw.js', { scope: '/' })
    .then((registration) => {
      registrationStatus = true;
      return registration;
    })
    .catch((error) => {
      registrationStatus = false;
      console.error('[workspace] Failed to register service worker', error);
      return null;
    });
  return registrationPromise;
}

/**
 * Like `ensureWorkspaceServiceWorker`, but resolves after `timeoutMs` even if
 * `navigator.serviceWorker.register()` never settles (seen in some Electron /
 * embedded-webview setups). Does not cancel the underlying registration attempt.
 *
 * @param {number} [timeoutMs]
 * @returns {Promise<{ timedOut: boolean, registration: unknown }>}
 */
export function ensureWorkspaceServiceWorkerWithTimeout(
  timeoutMs = DEFAULT_WORKSPACE_SW_ENSURE_TIMEOUT_MS
) {
  if (!supportsServiceWorker) {
    registrationStatus = false;
    return Promise.resolve({ timedOut: false, registration: null });
  }
  const cap = Math.max(1, Number(timeoutMs) || DEFAULT_WORKSPACE_SW_ENSURE_TIMEOUT_MS);
  return Promise.race([
    ensureWorkspaceServiceWorker().then((registration) => ({
      timedOut: false,
      registration,
    })),
    new Promise((resolve) => {
      setTimeout(() => resolve({ timedOut: true, registration: null }), cap);
    }),
  ]);
}

export function getServiceWorkerStatus() {
  return {
    supported: supportsServiceWorker,
    status: registrationStatus, // null = not attempted, true = success, false = failed
  };
}

export async function notifyWorkspaceServiceWorker(message) {
  if (!supportsServiceWorker) {
    return;
  }
  try {
    const registration = await ensureWorkspaceServiceWorker();
    if (!registration) {
      return;
    }
    const sw = registration.active || registration.waiting || registration.installing;
    if (sw) {
      sw.postMessage(message);
    }
  } catch (error) {
    console.warn('[workspace] Failed to notify service worker', error);
  }
}
